/**
 * @file pages/TermsPage.js
 * @description Public Terms of Service for the ButlerCore suite. Plain English,
 * one "human summary" line per clause, styled to the Butler Design Standard
 * (calm canvas, one blue, color only where it means something).
 *
 * NOT attorney-reviewed. Drafted to professional SaaS standard — a lawyer should
 * skim this before real customers click Agree.
 */
import React from 'react';
import PublicTopNav from '../components/PublicTopNav';
import PublicFooter from '../components/PublicFooter';

/* Design Standard tokens (Aug 2026): color = meaning, never decoration. */
const T = {
  canvas: '#F7F8FA',
  surface: '#FFFFFF',
  ink: '#171C22',
  muted: '#5C6570',
  line: '#E3E7EC',
  brand: '#0B6BCB',
};

const EFFECTIVE_DATE = 'August 13, 2026';

const styles = {
  page: { backgroundColor: T.canvas, color: T.ink, minHeight: '100vh' },
  wrap: { maxWidth: 820, margin: '0 auto', padding: '0 1.25rem 3rem' },
  hero: { textAlign: 'center', padding: '3rem 1.25rem 1.5rem', maxWidth: 820, margin: '0 auto' },
  h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 0.75rem' },
  lede: { color: T.muted, fontSize: '1.0625rem', lineHeight: 1.6, margin: 0 },
  stamp: {
    display: 'inline-block',
    marginTop: '1.25rem',
    color: T.muted,
    fontSize: '0.875rem',
    borderTop: `1px solid ${T.line}`,
    paddingTop: '0.75rem',
  },
  card: {
    backgroundColor: T.surface,
    border: `1px solid ${T.line}`,
    borderRadius: 14,
    padding: '1.4rem 1.5rem',
    marginBottom: '1.1rem',
    boxShadow: '0 1px 2px rgba(23,28,34,.05)',
  },
  h2: {
    fontSize: '1.15rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: T.brand,
    margin: '0 0 0.6rem',
  },
  summary: {
    borderLeft: `4px solid ${T.brand}`,
    background: '#F2F7FD',
    borderRadius: '0 10px 10px 0',
    padding: '0.6rem 0.9rem',
    margin: '0 0 0.9rem',
    fontWeight: 600,
    fontSize: '0.98rem',
    lineHeight: 1.5,
  },
  p: { margin: '0 0 0.85rem', lineHeight: 1.65, fontSize: '1rem' },
  last: { margin: 0, lineHeight: 1.65, fontSize: '1rem' },
  ul: { margin: '0 0 0.85rem', paddingLeft: '1.2rem', lineHeight: 1.65 },
  li: { marginBottom: '0.35rem' },
  footerNote: {
    textAlign: 'center',
    color: T.muted,
    fontSize: '0.8125rem',
    marginTop: '1.75rem',
    paddingTop: '1.25rem',
    borderTop: `1px solid ${T.line}`,
  },
  link: { color: T.brand },
};

/** One clause: a plain-English heading, a one-line human summary, then the detail. */
function Clause({ title, summary, children }) {
  return (
    <section style={styles.card}>
      <h2 style={styles.h2}>{title}</h2>
      <p style={styles.summary}>{summary}</p>
      {children}
    </section>
  );
}

function TermsPage() {
  return (
    <div style={styles.page}>
      <PublicTopNav />

      <header style={styles.hero}>
        <h1 style={styles.h1}>Terms of Service</h1>
        <p style={styles.lede}>
          The agreement between your company and ours, written the way we would say it out loud.
          Every section starts with a one-line summary. The lines underneath are the details, not
          a different deal.
        </p>
        <span style={styles.stamp}>
          Effective {EFFECTIVE_DATE} &nbsp;·&nbsp; ButlerCore, operated by WNCIL, LLC (North Carolina)
        </span>
      </header>

      <main style={styles.wrap}>

        <Clause
          title="1. Who we are, and what ButlerCore is"
          summary="ButlerCore is software for service businesses, built and run by WNCIL, LLC in Western North Carolina."
        >
          <p style={styles.p}>
            ButlerCore is a suite of connected web applications for service businesses. Today that
            suite includes tools for field inspections and their reports, scheduling and dispatch,
            notes, and water test reports. The suite is operated by WNCIL, LLC, a North Carolina
            limited liability company. In these terms, "we" and "us" mean WNCIL, LLC. "You" means
            the company that signs up, and the people that company invites to use it.
          </p>
          <p style={styles.p}>
            We describe what the software does honestly. Features that are still being built are
            labeled as such on the Features page, and a feature being planned is not a promise that
            it will ship on a date. If something in the product does not work the way this page or
            that page says it works, the product is the bug, and we want to hear about it.
          </p>
          <p style={styles.last}>
            Email from us comes from service@butlercore.com. That is the address that sends your
            reports, invitations, and account notices.
          </p>
        </Clause>

        <Clause
          title="2. Accounts and fair use"
          summary="One person per login. Your company's admins decide who gets in and who gets out."
        >
          <p style={styles.p}>
            An account belongs to one human being. Logins are not shared, not passed around the
            truck, and not handed to a subcontractor for the afternoon. This is not a licensing
            trick — it is how the record stays worth anything. When a report says who took the
            photo and who signed off, that name has to be a real person or the record is fiction.
          </p>
          <p style={styles.p}>
            Your company's administrators control access: they invite people, set what each person
            can see and do, and remove people who leave. When someone leaves your company, removing
            their access is your admins' job and their power — we do not need to be in the middle of
            it. Keep your passwords private, and tell us promptly if you think a login has been
            taken over.
          </p>
          <p style={styles.p}>Please do not use ButlerCore to:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>break the law, or help someone else break it;</li>
            <li style={styles.li}>upload material you have no right to upload;</li>
            <li style={styles.li}>attack, overload, or probe the service or other customers' data;</li>
            <li style={styles.li}>resell the service as your own product without a written agreement with us.</li>
          </ul>
          <p style={styles.last}>
            If an account is being used this way, we may suspend that account. We will tell you why,
            and we will work with you to fix it rather than disappear on you.
          </p>
        </Clause>

        <Clause
          title="3. Your data is yours"
          summary="Your business records belong to your company. We never delete them, we export them on request, and we only look when we are operating or supporting the service."
        >
          <p style={styles.p}>
            Everything your company puts into ButlerCore — inspections, reports, photos, customers,
            schedules, notes, water results — belongs to your company. Not to us. We hold it for
            you, we run the software around it, and that is the extent of our claim to it. We do not
            sell it, we do not rent it, and we do not train outside products on it.
          </p>
          <p style={styles.p}>
            <strong>We do not delete records.</strong> The suite is built append-only: when a record
            is changed or removed from view, the earlier version stays in the system as history — a
            "ghost" — rather than being erased. Deleting in ButlerCore means hiding from the working
            list, not destroying. This is deliberate. An inspection record that can be quietly
            rewritten is worthless the day it matters, and the day it matters is usually years
            later. If you need a record permanently destroyed, ask us and we will handle it as a
            deliberate, written request rather than a stray click.
          </p>
          <p style={styles.p}>
            <strong>Export on request.</strong> Ask us for your data and you get your data — your
            records and your files, in a common format, without an argument and without a fee. You
            do not have to be leaving to ask.
          </p>
          <p style={styles.p}>
            <strong>When we look.</strong> Our people access customer data only to operate the
            service, to fix something, or to answer a support request you raised — and only as much
            as the job needs. Data is scoped to your company: another ButlerCore customer cannot see
            your records. We use a small number of service providers to run the product, such as
            hosting and email delivery, and they are held to the same limits. That is our privacy
            practice in plain terms; if you need it in more detail for your own compliance
            paperwork, write to us and we will put it in writing.
          </p>
          <p style={styles.last}>
            No system is unbreakable, and we will not pretend otherwise. We protect your data with
            reasonable, current security measures, and if something happens that affects your data
            we will tell you promptly and honestly.
          </p>
        </Clause>

        <Clause
          title="4. Paying for it — and the founding price lock"
          summary="Subscriptions bill on a repeating cycle. If you came in as a founding customer at a locked price, that price is yours for as long as you keep the subscription."
        >
          <p style={styles.p}>
            ButlerCore is sold as a subscription. You pick a plan, it bills on a repeating cycle,
            and it renews until you cancel. Current prices live on the pricing page — deliberately
            not in this document, so a price change never quietly becomes a change to your legal
            terms. Fees are payable in advance and are not refundable for time already served,
            though if we have genuinely gotten something wrong we would rather make it right than
            win the argument.
          </p>
          <p style={styles.p}>
            <strong>The founding price lock.</strong> Customers who join at a founding price keep
            that price for the life of the subscription. When list prices go up — and they will —
            founding customers do not move. The lock stays with the company that earned it, for as
            long as that subscription stays active. If you cancel entirely and come back later, you
            come back at the price of that day. Adding modules or seats is priced at the rate for
            those additions; it does not reset the lock on what you already have.
          </p>
          <p style={styles.p}>
            For everyone else, we may change prices with at least 30 days' notice before a renewal,
            so you always get to decide before you pay.
          </p>
          <p style={styles.last}>
            If a payment fails, we will tell you and give you a fair chance to fix it before
            anything is limited. We do not lock a working crew out of their day over a declined
            card.
          </p>
        </Clause>

        <Clause
          title="5. Keeping it running"
          summary="We work hard to keep it up, but we do not promise perfect uptime — and offline mode still needs a signal to sync."
        >
          <p style={styles.p}>
            We run ButlerCore on a best-effort basis and we take it seriously: monitoring, backups,
            and a short leash on anything that breaks. What we will not do is print an uptime number
            we cannot honestly stand behind. There is no guaranteed 100% availability here. Servers
            go down, providers have bad days, and maintenance sometimes has to happen. When it does,
            we aim for the quietest hours and we say what happened.
          </p>
          <p style={styles.p}>
            <strong>About offline mode.</strong> Parts of the suite are built to keep working in a
            holler with no bars — you can keep entering an inspection with no signal, and the work
            is held on your device. That work syncs when the device gets a connection again. It is
            not magic: until it syncs, that data lives only on that phone or tablet. Do not wipe or
            replace a device with unsynced work on it, and give the app a moment on a real
            connection before you consider a job filed.
          </p>
          <p style={styles.last}>
            The product changes often. Features improve, screens get rebuilt, and occasionally
            something is retired. We give notice before removing anything you depend on.
          </p>
        </Clause>

        <Clause
          title="6. Reports and professional responsibility"
          summary="Your licensed professionals do the inspecting and sign the report. Our software is the tool they use — it is not the licensed party."
        >
          <p style={styles.p}>
            Inspection reports produced in ButlerCore are prepared, reviewed, and issued by the
            licensed professionals of your company. The judgment in a report is theirs. The license
            behind it is theirs. The responsibility to the customer, and to whatever board or agency
            regulates that work, is theirs.
          </p>
          <p style={styles.p}>
            We build the tool: the forms, the field registry, the photo handling, the templates, the
            review queue, and the suggestions the software offers along the way. Suggested wording,
            prompts, checks, and any AI-assisted drafting are aids to a professional, never a
            substitute for one. Nothing the software produces is a professional opinion, an
            inspection, a certification, or legal advice until a qualified person at your company
            has reviewed it and put their name on it.
          </p>
          <p style={styles.last}>
            Your company is responsible for holding the licenses its work requires, for following
            the rules of its trade and jurisdiction, and for what its reports say.
          </p>
        </Clause>

        <Clause
          title="7. Leaving"
          summary="You can cancel any time, you take your data with you, and we do not hold it hostage to keep you."
        >
          <p style={styles.p}>
            Cancel whenever you want. Your subscription runs to the end of the cycle you have paid
            for, and then it stops. There is no exit interview and no penalty for going.
          </p>
          <p style={styles.p}>
            On your way out, ask for an export and you get one — your records and your files, in a
            common format. We honor that request whether you are leaving happy or leaving annoyed.
            Holding a company's own records hostage to make leaving painful is not a business model
            we are willing to run.
          </p>
          <p style={styles.p}>
            After a cancellation, we keep your data available for a reasonable wind-down period so a
            late export request is not a disaster, and we keep the records our own legal and
            accounting obligations require. Ask us to destroy the rest and we will confirm in
            writing what was destroyed and when.
          </p>
          <p style={styles.last}>
            We may end an account ourselves for non-payment after notice, or for the serious misuse
            described in section 2. Even then, the export in this section still applies to you.
          </p>
        </Clause>

        <Clause
          title="8. What we are responsible for — and what we are not"
          summary="If we cause you a loss, our responsibility is capped at roughly what you paid us over the prior year, and neither side is on the hook for indirect losses."
        >
          <p style={styles.p}>
            The service is provided as it is. Apart from the promises actually made on this page, we
            do not make other warranties, express or implied, including implied warranties of
            merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p style={styles.p}>
            To the fullest extent the law allows, neither side is liable to the other for indirect,
            incidental, special, or consequential damages, or for lost profits, lost business, or
            lost data, even if the possibility was known. Our total liability arising out of or
            related to these terms or the service is limited to the amount you paid us for the
            service in the twelve months before the event that gave rise to the claim.
          </p>
          <p style={styles.last}>
            Some of these limits do not apply where the law says they cannot — for example to our
            own gross negligence, willful misconduct, or fraud. Nothing here is an attempt to dodge
            that.
          </p>
        </Clause>

        <Clause
          title="9. The law that applies"
          summary="North Carolina law governs this agreement, and disputes belong to the courts of North Carolina."
        >
          <p style={styles.last}>
            These terms are governed by the laws of the State of North Carolina, without regard to
            its conflict-of-laws rules. Any dispute arising out of or relating to these terms or the
            service will be brought in the state or federal courts located in North Carolina, and
            both sides consent to that. If any part of these terms is found unenforceable, the rest
            of it stays in force.
          </p>
        </Clause>

        <Clause
          title="10. Changes to these terms"
          summary="We can update these terms, but not in secret — meaningful changes come with at least 30 days' notice."
        >
          <p style={styles.p}>
            As the product grows, this page will need to change. When we make a change that
            meaningfully affects your rights or obligations, we will give you at least 30 days'
            notice by email to your account administrators and by posting the updated page with a
            new effective date. Small corrections — a typo, a clearer sentence — we will simply fix.
          </p>
          <p style={styles.last}>
            If you keep using ButlerCore after a change takes effect, that is your acceptance of the
            updated terms. If you would rather not accept them, cancel before the effective date and
            take your export with you under section 7.
          </p>
        </Clause>

        <Clause
          title="11. Questions"
          summary="Ask a person. We answer."
        >
          <p style={styles.last}>
            Questions:{' '}
            <a href="mailto:service@butlercore.com" style={styles.link}>service@butlercore.com</a>
            . These terms are between your company and WNCIL, LLC, North Carolina. They are the
            whole agreement between us about the service, and they replace any earlier
            understanding about it.
          </p>
        </Clause>

        <p style={styles.footerNote}>Plain-English terms, version 1.</p>
      </main>

      <PublicFooter />
    </div>
  );
}

export default TermsPage;
